import getArticles from "@/api/requests/wordpress/getArticles";
import getAuthor from "@/api/requests/wordpress/getAuthor";
import getMenuItems from "@/api/requests/wordpress/getMenuItems";
import getYoastRedirect from "@/api/requests/wordpress/getYoastRedirect";
import {
  ArticleExcerpt,
  AuthorPage as AuthorPageType,
  MenuItems,
} from "@/custom-types/Wordpress";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getUrlPath } from "@/utils/getUrlPath";
import { getCountryCodeHeader } from "@/utils/requestContext";

import AuthorBox from "@/components/news/AuthorBox";
import ListLayout from "@/components/news/Layouts/ListLayout";
import PageLayout from "@/components/news/Layouts/PageLayout";

type AuthorPageProps = {
  menuItems: MenuItems;
  author: AuthorPageType;
  articles: ArticleExcerpt[];
  totalCount: number;
  totalPages: number;
};

const AuthorPage: React.FC<AuthorPageProps> = ({
  author,
  articles,
  menuItems,
  totalCount,
  totalPages,
}) => {
  const breadcrumbs = [
    {
      href: "/news",
      title: "News",
    },
    {
      href: `/news/author/${author.slug}`,
      title: author.title,
    },
  ];

  return (
    <PageLayout
      menuItems={menuItems}
      page={{ ...author, breadcrumbs }}
      currentPage={1}
      totalPages={totalPages}
    >
      <AuthorBox
        author={{ ...author, name: author.title }}
        className="mb-xxl"
      />
      <ListLayout
        articles={articles}
        totalCount={totalCount}
        currentPage={1}
        totalPages={totalPages}
        slug={author.slug}
      />
    </PageLayout>
  );
};

export const getServerSideProps = createGetServerSideProps<
  AuthorPageProps,
  {
    authorSlug: string;
  }
>(async (context) => {
  const { resolvedUrl, params } = context;
  const redirect = await getYoastRedirect(getUrlPath(resolvedUrl));

  if (redirect?.destination) {
    return {
      redirect,
    };
  }

  if (!params?.authorSlug) {
    return {
      notFound: true,
    };
  }

  const author = await getAuthor(params.authorSlug);

  if (!author) {
    return {
      notFound: true,
    };
  }

  const countryCode = getCountryCodeHeader(context.req.headers);
  const [{ articles, totalCount, totalPages }, menuItems] = await Promise.all([
    getArticles(countryCode, {
      author: `${author.id}`,
    }),
    getMenuItems(countryCode === "CA" ? "canada" : "global"),
  ]);

  return {
    props: {
      articles: articles as ArticleExcerpt[],
      author,
      menuItems,
      totalCount,
      totalPages,
    },
  };
});

export default AuthorPage;
